import { FormChildProp } from '@/components/Form/types';
import React, { useEffect, useState } from 'react';
import { IconButton, Text, useTheme } from 'react-native-paper';
import { StyleSheet, View } from 'react-native';
import { Color as ThemeColors, Padding } from '@/GlobalStyles';
import Slider, { SliderProps } from '@react-native-community/slider';
import color from 'color';
import { useTranslation } from 'react-i18next';
import HelpButton from '@/components/HelpButton';

type BeforeAction = (
  currentValue: number,
  nextValue: number,
  callback: (value: number) => void
) => void;
const PlusMinusInput = React.forwardRef(
  (
    props: Omit<SliderProps, 'value' | 'onValueChange'> &
      FormChildProp & {
        defaultValue?: number;
        value?: number;
        unit?: string;
        invertOutput?: boolean;
        beforeAdd?: BeforeAction;
        beforeSub?: BeforeAction;
      },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ref
  ): React.ReactElement => {
    const {
      defaultValue,
      onChangeValue,
      error,
      containerStyle,
      value,
      name,
      label,
      labelStyle,
      unit,
      maximumValue = Infinity,
      minimumValue = 0,
      invertOutput = false,
      step = 1,
      beforeAdd = (currentValue, value, callback) => callback(value),
      beforeSub = (currentValue, value, callback) => callback(value),
      withHelpButton = false,
      helpFieldName,
    } = props;
    const theme = useTheme();

    const [valueSet, setValueSet] = useState<number | undefined>(
      (value !== undefined ? value : defaultValue) || 0
    );

    const { t } = useTranslation();

    useEffect(() => {
      if (onChangeValue && valueSet !== value) {
        const finalValue =
          (valueSet !== undefined ? valueSet : value !== undefined ? value : defaultValue) || 0;
        onChangeValue(finalValue);
      }
    }, [valueSet]);

    return (
      <View
        style={[
          {
            flexDirection: 'column',
            alignItems: 'center',
          },
          containerStyle,
        ]}
      >
        {label ? (
          withHelpButton && (helpFieldName || name) ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 5 }}>
              <Text variant={'labelMedium'} style={[styles.label, { marginBottom: 0 }, labelStyle]}>
                {t(label)}
              </Text>
              <HelpButton
                fieldName={helpFieldName || name || ''}
                size={16}
                style={{ marginLeft: 4 }}
              />
            </View>
          ) : (
            <Text variant={'labelMedium'} style={[styles.label, labelStyle]}>
              {t(label)}
            </Text>
          )
        ) : null}
        <View style={styles.buttons}>
          <IconButton
            icon={'minus'}
            containerColor={color(ThemeColors.MainBleu).lighten(1.5).hex()}
            onPress={() =>
              beforeSub(valueSet, Math.max(valueSet - step, minimumValue), setValueSet)
            }
            iconColor={ThemeColors.MainBleu}
          />
          <Text style={{ width: 55, textAlign: 'center' }} variant={'labelLarge'}>
            {(valueSet || 0) > 0 ? '+' : ''}
            {valueSet} {unit}
          </Text>
          <IconButton
            icon={'plus'}
            containerColor={color(ThemeColors.MainBleu).lighten(1.5).hex()}
            onPress={() =>
              beforeAdd(valueSet, Math.min(valueSet + step, maximumValue), setValueSet)
            }
            iconColor={ThemeColors.MainBleu}
          />
        </View>
        {error?.message ? (
          <Text
            variant={'labelMedium'}
            style={{
              color: theme.colors.error,
              marginLeft: 18,
              bottom: -18,
              flex: 1,
              position: 'absolute',
            }}
          >
            {props.error?.message}
          </Text>
        ) : null}
      </View>
    );
  }
);

export default PlusMinusInput;

const styles = StyleSheet.create({
  label: {
    marginLeft: Padding.p_mini,
  },
  buttons: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: 20,
  },
});
